import useItemSalesReport from "@/hooks/useItemSalesReport";
import HighlightText from "@/shared/components/HighlightText";
import DatePicker from "@/shared/components/ui/DatePicker";
import { Dropdown, DropdownItem } from "@/shared/components/ui/Dropdown";
import { motion } from 'framer-motion';
import {
    BadgePercent,
    Loader, Search, ShoppingBag,
    Tag,
    Truck, ArrowDownWideNarrow, ArrowUpNarrowWide,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import ReactPaginate from "react-paginate";

export default function ItemSalesReport() {
    const { t } = useTranslation();
    const {
        date, setDate, handleQuickSelect,
        selectedProject, setSelectedProject, projectsWithBusiness,
        selectedColor, setSelectedColor, colors,
        selectedSize, setSelectedSize,
        selectedEmployee, setSelectedEmployee, users,
        zeroDeliveryOnly, setZeroDeliveryOnly,
        discountedOnly, setDiscountedOnly,
        searchValue, setSearchValue,
        page, setPage, totalPages, totalRows, pageSize,
        rows,
        isLoading, handleExport, isSearching,
        sortOrder,
        setSortOrder,
    } = useItemSalesReport();


    return (
        <>
            {/* <Header title={t('item_sales_report')} /> */}

            {isLoading ? <Loader /> : (
                < >
                    <div className="w-full max-w-7xl">
                        <div className="mb-4 flex items-center justify-between bg-slate-50 border border-slate-200/60 rounded-xl p-1.5 gap-2">
                            <div className="flex items-center gap-2 text-sm bg-white border border-slate-200 shadow-sm px-3 py-1.5 rounded-lg font-semibold text-slate-700">
                                <Tag className="w-4 h-4 text-violet-500" />
                                <span>{t("total_results")}:</span>
                                <span className="text-violet-600 font-bold">{totalRows}</span>
                            </div>


                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setZeroDeliveryOnly(v => !v)}
                                    className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all ${zeroDeliveryOnly
                                        ? 'bg-emerald-500 text-white border-emerald-500'
                                        : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300'
                                        }`}
                                >
                                    <Truck className="w-3.5 h-3.5" />
                                    {t("zero_delivery_fees")}
                                </button>

                                <button
                                    onClick={() => setDiscountedOnly(v => !v)}
                                    className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all ${discountedOnly
                                        ? 'bg-rose-500 text-white border-rose-500'
                                        : 'bg-white text-slate-600 border-slate-200 hover:border-rose-300'
                                        }`}
                                >
                                    <BadgePercent className="w-3.5 h-3.5" />
                                    {t("discounted_below_cost")}
                                </button>
                            </div>

                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => handleExport('pdf', 'تقرير المبيعات والأرباح')}
                                    className="bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-sm transition-colors"
                                >

                                    تصدير PDF
                                </button>
                                <button
                                    onClick={() => handleExport('excel', 'تقرير المبيعات والأرباح')}
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-sm transition-colors"
                                >

                                    تصدير Excel
                                </button>
                            </div>
                        </div>

                        <div className="mb-6 w-full max-w-7xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">

                            <div className="flex flex-col gap-1.5 w-full">
                                <div className="flex items-center justify-between">
                                    <label className="text-xs font-semibold text-slate-500">{t("start_date")}</label>
                                    <Dropdown label={t("quick_select")}>
                                        <DropdownItem onClick={() => handleQuickSelect('today')}>{t("today")}</DropdownItem>
                                        <DropdownItem onClick={() => handleQuickSelect('yesterday')}>{t("yesterday")}</DropdownItem>
                                        <DropdownItem onClick={() => handleQuickSelect('current_month')}>{t("current month")}</DropdownItem>
                                        <DropdownItem onClick={() => handleQuickSelect('last_month')}>{t("before month")}</DropdownItem>
                                    </Dropdown>
                                </div>
                                <DatePicker id="isr-from" label="" placeholder={t("Select a date")}
                                    onChange={(_, d) => setDate(p => ({ ...p, from: d }))} defaultDate={date.from} />
                            </div>

                            <div className="flex flex-col gap-1.5 w-full">
                                <div className="flex items-center justify-between">
                                    <label className="text-xs font-semibold text-slate-500">{t("end_date")}</label>
                                    <Dropdown label={t("quick_select")}>
                                        <DropdownItem onClick={() => handleQuickSelect('today')}>{t("today")}</DropdownItem>
                                        <DropdownItem onClick={() => handleQuickSelect('yesterday')}>{t("yesterday")}</DropdownItem>
                                        <DropdownItem onClick={() => handleQuickSelect('current_month')}>{t("current month")}</DropdownItem>
                                        <DropdownItem onClick={() => handleQuickSelect('last_month')}>{t("before month")}</DropdownItem>
                                    </Dropdown>
                                </div>
                                <DatePicker id="isr-to" label="" placeholder={t("Select a date")}
                                    onChange={(_, d) => setDate(p => ({ ...p, to: d }))} defaultDate={date.to} />
                            </div>

                            <div className="flex flex-col gap-1.5 w-full">
                                <label className="text-xs font-semibold text-slate-500">{t("store")}</label>
                                <div className="relative w-full">
                                    <select
                                        value={selectedProject}
                                        onChange={(e) => setSelectedProject(e.target.value)}
                                        className="w-full appearance-none bg-white border border-slate-200 rounded-xl px-4 py-2.5 pr-10 text-sm text-slate-900 font-medium shadow-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-400 outline-none cursor-pointer transition-all hover:border-slate-300"
                                    >
                                        <option value="">{t("all_stores")}</option>
                                        {projectsWithBusiness?.map((project: any, index: number) => (
                                            <option key={index} value={project?.business_id}>
                                                {project.type_name}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                        <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col gap-1.5 w-full">
                                <label className="text-xs font-semibold text-slate-500">{t("employee")}</label>
                                <div className="relative w-full">
                                    <select
                                        value={selectedEmployee}
                                        onChange={(e) => setSelectedEmployee(e.target.value)}
                                        className="w-full appearance-none bg-white border border-slate-200 rounded-xl px-4 py-2.5 pr-10 text-sm text-slate-900 font-medium shadow-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-400 outline-none cursor-pointer transition-all hover:border-slate-300"
                                    >
                                        <option value="">{t("all_employees")}</option>
                                        {users?.map((user: any, index: number) => (
                                            <option key={index} value={user?.type_id}>
                                                {user.type_name}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                        <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col gap-1.5 w-full">
                                <label className="text-xs font-semibold text-slate-500">{t("color")}</label>
                                <div className="relative w-full">
                                    <select
                                        value={selectedColor}
                                        onChange={(e) => setSelectedColor(e.target.value)}
                                        className="w-full appearance-none bg-white border border-slate-200 rounded-xl px-4 py-2.5 pr-10 text-sm text-slate-900 font-medium shadow-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-400 outline-none cursor-pointer transition-all hover:border-slate-300"
                                    >
                                        <option value="">{t("all_colors")}</option>
                                        {colors?.map((color: any, index: number) => (
                                            <option key={index} value={color?.type_name}>
                                                {color.type_name}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                        <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col gap-1.5 w-full">
                                <label className="text-xs font-semibold text-slate-500">{t("size")}</label>
                                <input
                                    value={selectedSize}
                                    onChange={(e) => setSelectedSize(e.target.value)}
                                    type="text"
                                    placeholder={t("size")}
                                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-400 shadow-sm transition-all hover:border-slate-300"
                                />
                            </div>

                            <div className="flex flex-col gap-1.5 w-full lg:col-span-2">
                                <label className="text-xs font-semibold text-slate-500">{t("search")}</label>
                                <div className="relative w-full">
                                    <input
                                        value={searchValue}
                                        onChange={(e) => setSearchValue(e.target.value)}
                                        type="text"
                                        placeholder={t("search_by_item_or_employee")}
                                        className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 shadow-sm transition-all hover:border-slate-300"
                                    />
                                    <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
                                        {isSearching ? (
                                            <Loader className="w-4 h-4 text-emerald-500 animate-spin" />
                                        ) : (
                                            <Search className="w-4 h-4 text-slate-400" />
                                        )}
                                    </div>

                                </div>
                            </div>

                            <div className="flex flex-col gap-1.5 w-full">
                                <label className="text-xs font-semibold text-slate-500">{t("ترتيب حسب التاريخ")}</label>
                                <button
                                    type="button"
                                    onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                                    className="w-full flex items-center justify-center gap-2 h-[42px] px-4 rounded-xl border text-sm font-medium bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 shadow-sm transition-all duration-200 active:scale-[0.98] cursor-pointer"
                                >
                                    {sortOrder === 'asc' ? (
                                        <>
                                            <ArrowUpNarrowWide className="w-4 h-4 text-violet-600" />
                                            <span>{t("الأقدم للأحدث")}</span>
                                        </>
                                    ) : (
                                        <>
                                            <ArrowDownWideNarrow className="w-4 h-4 text-violet-600" />
                                            <span>{t("الأحدث للأقدم")}</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="bg-white rounded-[1rem] border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden">


                            <div className={`overflow-x-auto transition-opacity duration-200 ${isSearching ? 'opacity-50' : 'opacity-100'}`}>
                                <table className="w-full text-right border-collapse">
                                    <thead>
                                        <tr className="text-slate-800 uppercase font-black tracking-widest border-b border-slate-100 bg-slate-50/30">
                                            <th className="px-6 py-4">{t("item_name")}</th>
                                            <th className="px-6 py-4">{t("color")}</th>
                                            <th className="px-6 py-4">{t("size")}</th>
                                            <th className="px-6 py-4">{t("store")}</th>
                                            <th className="px-6 py-4">{t("employee")}</th>
                                            <th className="px-6 py-4">{t("order_date")}</th>
                                            <th className="px-6 py-4">{t("original_price")}</th>
                                            <th className="px-6 py-4">{t("sell_price")}</th>
                                            <th className="px-6 py-4">{t("delivery_fees")}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {rows?.length === 0 ? (
                                            <tr>
                                                <td colSpan={9} className="px-6 py-16 text-center">
                                                    <div className="flex flex-col items-center gap-2 text-slate-400">
                                                        <ShoppingBag className="w-10 h-10 text-slate-300" />
                                                        <span className="text-sm font-medium text-slate-500">{t("no_available_results")}</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            rows?.map((row: any, index: number) => {
                                                const isDiscounted = Number(row.sell_price) < Number(row.original_price);
                                                const isFreeDelivery = Number(row.delivery_fees) === 0;
                                                return (
                                                    <motion.tr
                                                        key={`${row.item_name}-${index}`}
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ delay: index * 0.02 }}
                                                        className="hover:bg-slate-50/50 transition-colors"
                                                    >
                                                        <td className="px-6 py-4 text-sm font-medium text-slate-900">
                                                            <HighlightText search={searchValue} text={row?.item_name || "---"} />
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-slate-600">{row?.item_color || "---"}</td>
                                                        <td className="px-6 py-4 text-sm text-slate-600">{row?.item_size || "---"}</td>
                                                        <td className="px-6 py-4 text-sm text-slate-600">{row?.project_id || "---"}</td>
                                                        <td className="px-6 py-4 text-sm text-slate-600">
                                                            <HighlightText search={searchValue} text={row?.user_name || "---"} />
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-slate-500 whitespace-nowrap">{row?.order_date || "---"}</td>
                                                        <td className="px-6 py-4 text-sm text-slate-500">{row?.original_price ?? "---"}</td>
                                                        <td className={`px-6 py-4 text-sm font-semibold ${isDiscounted ? 'text-rose-600' : 'text-slate-900'}`}>
                                                            {row?.sell_price ?? "---"}
                                                            {isDiscounted && (
                                                                <span className="mr-1.5 inline-block text-[10px] font-bold bg-rose-50 text-rose-600 px-1.5 py-0.5 rounded">
                                                                    {t("below_cost")}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className={`px-6 py-4 text-sm ${isFreeDelivery ? 'text-emerald-600 font-semibold' : 'text-slate-500'}`}>
                                                            {row?.delivery_fees ?? "---"}
                                                        </td>
                                                    </motion.tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>


                            {totalPages > 1 && (
                                <div className="flex items-center justify-between px-6 py-3 border-t border-slate-100 bg-slate-50/30">
                                    <span className="text-xs text-slate-500">
                                        {page * pageSize + 1}–{Math.min((page + 1) * pageSize, totalRows)} {t("of")} {totalRows}
                                    </span>
                                    <ReactPaginate
                                        pageCount={totalPages}
                                        forcePage={page}
                                        onPageChange={({ selected }) => setPage(selected)}
                                        marginPagesDisplayed={1}
                                        pageRangeDisplayed={3}
                                        previousLabel="‹"
                                        nextLabel="›"
                                        breakLabel="…"
                                        containerClassName="flex items-center gap-1"
                                        pageClassName="inline-flex cursor-pointer"
                                        pageLinkClassName="min-w-[2rem] h-8 px-2 flex items-center justify-center rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-200 transition-colors"
                                        activeClassName="!text-white"
                                        activeLinkClassName="!bg-violet-600 !text-white shadow-sm"
                                        previousClassName="inline-flex"
                                        previousLinkClassName="cursor-pointer min-w-[2rem] h-8 px-2 flex items-center justify-center rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-200 transition-colors"
                                        nextClassName="inline-flex"
                                        nextLinkClassName="cursor-pointer min-w-[2rem] h-8 px-2 flex items-center justify-center rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-200 transition-colors"
                                        breakClassName="inline-flex"
                                        breakLinkClassName="min-w-[2rem] h-8 px-2 flex items-center justify-center text-slate-400 text-sm select-none"
                                        disabledClassName="opacity-30 cursor-not-allowed pointer-events-none"
                                        renderOnZeroPageCount={null}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </>
    )
}